import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, GlobeIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useLanguage } from '../LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: t('nav_home'),
    href: '#home'
  }, {
    name: t('nav_services'),
    href: '#services'
  }, {
    name: t('nav_about'),
    href: '#about'
  }, {
    name: t('nav_case_studies'),
    href: '#case-studies'
  }, {
    name: t('nav_contact'),
    href: '#contact'
  }];
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-242">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-243">
        <div className="flex justify-between items-center" data-id="element-244">
          <a href="#home" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-electric rounded-sm" data-id="element-245">
            <Logo className="h-8 md:h-10" light={!isScrolled} data-id="element-246" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-247">
            <ul className="flex items-center gap-6" data-id="element-248">
              {navLinks.map(link => <li key={link.name} data-id="element-249">
                  <a href={link.href} className={`text-sm font-medium transition-colors hover:text-electric focus:outline-none focus-visible:ring-2 focus-visible:ring-electric rounded-sm px-1 ${isScrolled ? 'text-slate-600' : 'text-slate-200'}`} data-id="element-250">
                    {link.name}
                  </a>
                </li>)}
            </ul>

            <div className="flex items-center gap-4 border-l border-slate-300/30 pl-4" data-id="element-251">
              <button onClick={toggleLanguage} className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-electric focus:outline-none focus-visible:ring-2 focus-visible:ring-electric rounded-sm px-1 ${isScrolled ? 'text-slate-600' : 'text-slate-200'}`} aria-label="Toggle language" data-id="element-252">
                <GlobeIcon className="w-4 h-4" data-id="element-253" />
                <span className="uppercase" data-id="element-254">{language}</span>
              </button>

              <a href="#contact" className="bg-electric hover:bg-electric-hover text-white px-5 py-2.5 rounded-md font-medium text-sm transition-all shadow-lg shadow-electric/20 hover:shadow-electric/40 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-electric" data-id="element-255">
                {t('nav_get_started')}
              </a>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-4 md:hidden" data-id="element-256">
            <button onClick={toggleLanguage} className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-electric focus:outline-none focus-visible:ring-2 focus-visible:ring-electric rounded-sm px-1 ${isScrolled ? 'text-slate-600' : 'text-slate-200'}`} data-id="element-257">
              <GlobeIcon className="w-5 h-5" data-id="element-258" />
              <span className="uppercase" data-id="element-259">{language}</span>
            </button>
            <button className={`p-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-electric ${isScrolled ? 'text-navy' : 'text-white'}`} onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" data-id="element-260">
              <MenuIcon className="w-6 h-6" data-id="element-261" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence data-id="element-262">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-navy/80 backdrop-blur-sm z-50 md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-263" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-[280px] bg-white z-50 shadow-2xl flex flex-col md:hidden" data-id="element-264">
              <div className="p-5 flex justify-between items-center border-b border-slate-100" data-id="element-265">
                <Logo className="h-8" data-id="element-266" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-slate-500 hover:text-navy rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-electric" aria-label="Close menu" data-id="element-267">
                  <XIcon className="w-6 h-6" data-id="element-268" />
                </button>
              </div>
              <div className="p-5 flex flex-col gap-4 flex-1 overflow-y-auto" data-id="element-269">
                {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-navy py-2 border-b border-slate-50 hover:text-electric transition-colors" data-id="element-270">
                    {link.name}
                  </a>)}
              </div>
              <div className="p-5 border-t border-slate-100" data-id="element-271">
                <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="flex justify-center w-full bg-electric hover:bg-electric-hover text-white px-5 py-3 rounded-md font-medium transition-colors" data-id="element-272">
                  {t('nav_get_started')}
                </a>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </header>;
}